import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { 
  MessageSquare, 
  Search, 
  Users, 
  Calendar,
  Eye,
  AlertTriangle,
  Phone,
  Mail,
  User,
  Camera,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";

interface Message {
  id: string;
  content: string;
  message_type: string;
  photo_url?: string;
  is_blocked: boolean;
  blocked_reason?: string;
  created_at: string;
  sender: {
    id: string;
    full_name: string;
    user_type: string;
    email: string;
    phone?: string;
  };
  recipient: {
    id: string;
    full_name: string;
    user_type: string;
  };
}

interface JobConversation {
  job_id: string;
  job_title: string;
  job_status: string;
  customer: {
    id: string;
    full_name: string;
    email: string;
    phone?: string;
  };
  tradesman?: {
    id: string;
    full_name: string;
    email: string;
    phone?: string;
  };
  messages: Message[];
  message_count: number;
  last_message_at: string;
}

export function AdminChatManagement() {
  const [conversations, setConversations] = useState<JobConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<JobConversation | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { isAdmin, isLoading: authLoading } = useAdminAuth();

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchConversations();
    }
  }, [authLoading, isAdmin]);

  const fetchConversations = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching job conversations for admin");

      // First, get all jobs that have messages
      const { data: jobsWithMessages, error: jobError } = await supabase
        .from("jobs")
        .select(`
          id,
          title,
          status,
          customer:profiles!jobs_customer_id_fkey(id, full_name, email, phone),
          tradesman:profiles!jobs_tradesman_id_fkey(id, full_name, email, phone)
        `)
        .in('id', 
          await supabase
            .from('messages')
            .select('job_id')
            .then(res => res.data?.map(m => m.job_id) || [])
        );

      if (jobError) {
        console.error("Error fetching jobs:", jobError);
        throw jobError;
      }

      const jobConversations: JobConversation[] = [];

      // For each job, get the messages and create conversation objects
      for (const job of jobsWithMessages || []) {
        const { data: messages, error: messageError } = await supabase
          .from("messages")
          .select(`
            id,
            content,
            message_type,
            photo_url,
            is_blocked,
            blocked_reason,
            created_at,
            sender:profiles!messages_sender_id_fkey(id, full_name, user_type, email, phone),
            recipient:profiles!messages_recipient_id_fkey(id, full_name, user_type)
          `)
          .eq("job_id", job.id)
          .order("created_at", { ascending: true });

        if (messageError) {
          console.error(`Error fetching messages for job ${job.id}:`, messageError);
          continue;
        }

        if (messages && messages.length > 0) {
          jobConversations.push({
            job_id: job.id,
            job_title: job.title,
            job_status: job.status,
            customer: job.customer,
            tradesman: job.tradesman,
            messages: messages,
            message_count: messages.length,
            last_message_at: messages[messages.length - 1]?.created_at || ""
          });
        }
      }

      // Sort by last message time (most recent first)
      jobConversations.sort((a, b) => 
        new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
      );

      setConversations(jobConversations);
      console.log(`Loaded ${jobConversations.length} conversations`);
    } catch (error: any) {
      console.error("Error fetching conversations:", error);
      toast({
        title: "Error loading conversations",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = searchTerm === "" || 
      conv.job_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.customer.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.tradesman?.full_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || conv.job_status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800";
      case "quoted": return "bg-blue-100 text-blue-800";
      case "quote_accepted": return "bg-purple-100 text-purple-800";
      case "assigned": return "bg-indigo-100 text-indigo-800";
      case "in_progress": return "bg-orange-100 text-orange-800";
      case "completed": return "bg-green-100 text-green-800";
      case "cancelled": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getMessageTypeIcon = (type: string) => {
    switch (type) {
      case "photo": return <Camera className="h-3 w-3" />;
      default: return <MessageSquare className="h-3 w-3" />;
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-lg font-medium">Access Denied</p>
        <p className="text-muted-foreground">You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Chat Management</h2>
          <p className="text-muted-foreground">Monitor conversations between customers and tradesmen</p>
        </div>
        <Button onClick={fetchConversations} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search conversations</label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                <Input
                  placeholder="Search by job title, customer, or tradesman..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Job Status</label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="quoted">Quoted</SelectItem>
                  <SelectItem value="quote_accepted">Quote Accepted</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conversations List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Conversations ({filteredConversations.length})
            </CardTitle>
            <CardDescription>
              Click on a conversation to view messages
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[600px]">
              {isLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium">No conversations found</p>
                  <p className="text-muted-foreground">No messages have been exchanged yet.</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredConversations.map((conversation) => (
                    <div
                      key={conversation.job_id}
                      className={`p-4 border-b cursor-pointer hover:bg-muted/50 transition-colors ${
                        selectedConversation?.job_id === conversation.job_id ? 'bg-muted' : ''
                      }`}
                      onClick={() => setSelectedConversation(conversation)}
                    >
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-sm truncate">{conversation.job_title}</h4>
                          <Badge className={`text-xs ${getJobStatusColor(conversation.job_status)}`}>
                            {conversation.job_status.replace('_', ' ')}
                          </Badge>
                        </div>
                        
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <User className="h-3 w-3" />
                            <span>Customer: {conversation.customer.full_name}</span>
                          </div>
                          {conversation.tradesman && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <User className="h-3 w-3" />
                              <span>Tradesman: {conversation.tradesman.full_name}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <MessageSquare className="h-3 w-3" />
                            <span>{conversation.message_count} messages</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            <span>{format(new Date(conversation.last_message_at), 'MMM d, HH:mm')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Message Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Conversation Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedConversation ? (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-muted-foreground">Choose a conversation from the list to view messages.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Conversation Header */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium">{selectedConversation.job_title}</h3>
                    <Badge className={getJobStatusColor(selectedConversation.job_status)}>
                      {selectedConversation.job_status.replace('_', ' ')}
                    </Badge>
                  </div>

                  {/* Participants */}
                  <div className="grid grid-cols-1 gap-3 p-3 bg-muted rounded-lg">
                    <div>
                      <h4 className="text-sm font-medium text-blue-600 mb-1">Customer</h4>
                      <p className="text-sm font-medium">{selectedConversation.customer.full_name}</p>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <span>{selectedConversation.customer.email}</span>
                        </div>
                        {selectedConversation.customer.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            <span>{selectedConversation.customer.phone}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {selectedConversation.tradesman && (
                      <div>
                        <h4 className="text-sm font-medium text-green-600 mb-1">Tradesman</h4>
                        <p className="text-sm font-medium">{selectedConversation.tradesman.full_name}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            <span>{selectedConversation.tradesman.email}</span>
                          </div>
                          {selectedConversation.tradesman.phone && (
                            <div className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              <span>{selectedConversation.tradesman.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Messages */}
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {selectedConversation.messages.map((message) => (
                      <div
                        key={message.id}
                        className={`p-3 rounded-lg ${
                          message.sender.user_type === 'customer' 
                            ? 'bg-blue-50 border-l-4 border-l-blue-500' 
                            : 'bg-green-50 border-l-4 border-l-green-500'
                        } ${message.is_blocked ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              {getMessageTypeIcon(message.message_type)}
                              <span className="text-sm font-medium">
                                {message.sender.full_name}
                              </span>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              {message.sender.user_type}
                            </Badge>
                            {message.is_blocked && (
                              <Badge variant="destructive" className="text-xs">
                                Blocked
                              </Badge>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(message.created_at), 'MMM d, HH:mm')}
                          </span>
                        </div>

                        {message.message_type === 'photo' && message.photo_url ? (
                          <div className="space-y-2">
                            <img 
                              src={message.photo_url} 
                              alt="Shared photo" 
                              className="max-w-xs rounded-lg border"
                            />
                            {message.content && (
                              <p className="text-sm">{message.content}</p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm">{message.content}</p>
                        )}

                        {message.is_blocked && message.blocked_reason && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            <strong>Blocked reason:</strong> {message.blocked_reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}